import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Leaf, Factory, Cpu, TrendingDown, Flame, Wind, 
  BarChart3, FileCheck, Download, MapPin, Users, Zap 
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BioCNGPlantPage = () => {
  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')`,
        }}
      >
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <div className="mb-4">
              <span className="text-xs font-mono tracking-widest uppercase text-orange-500 bg-orange-500/10 border border-orange-500/20 px-3 py-1.5 inline-block">
                UPCOMING PROJECT
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter uppercase leading-[0.9] mb-6 font-heading">
              Turning Organic Waste into <span className="text-lime-400">Clean Fuel</span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-8 text-zinc-300">
              Intelligently
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-zinc-300 mb-8">
              GreenPure's Smart Bio-CNG Plant in Chikkaballapur — 3 TPD capacity converting agricultural and biomass waste into high-purity Bio-CNG fuel
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-zinc-900/80 border border-lime-400/20 p-4">
                <p className="text-3xl font-bold text-lime-400">95%+</p>
                <p className="text-sm text-zinc-400 uppercase tracking-wider">Methane Purity</p>
              </div>
              <div className="bg-zinc-900/80 border border-lime-400/20 p-4">
                <p className="text-3xl font-bold text-lime-400">3 TPD</p>
                <p className="text-sm text-zinc-400 uppercase tracking-wider">Processing Capacity</p>
              </div>
              <div className="bg-zinc-900/80 border border-lime-400/20 p-4">
                <p className="text-3xl font-bold text-lime-400">70-80%</p>
                <p className="text-sm text-zinc-400 uppercase tracking-wider">Lower Carbon Emissions</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <a
                href="#contact"
                className="bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1"
                data-testid="cta-schedule-visit"
              >
                Schedule Site Visit
              </a>
              <a
                href="#process"
                className="bg-transparent border-2 border-zinc-700 text-white hover:border-lime-400 hover:text-lime-400 font-bold uppercase tracking-wider px-8 py-4 transition-colors"
              >
                How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Process Flow */}
      <section id="process" className="py-16 md:py-24 bg-zinc-950">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              How It <span className="text-lime-400">Works</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Advanced 7-stage process converting waste to clean Bio-CNG fuel
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Feedstock Collection",
                desc: "Agricultural residues, biomass waste, food/organic waste sourced locally from Chikkaballapur region",
                icon: <Leaf className="w-6 h-6" />
              },
              {
                step: 2,
                title: "Pre-treatment & Slurry Preparation",
                desc: "Shredding, screening, and slurry mixing for optimal digestion",
                icon: <Factory className="w-6 h-6" />
              },
              {
                step: 3,
                title: "Anaerobic Digestion (AD)",
                desc: "Multi-stage digester with controlled temperature (mesophilic/thermophilic), pH management, and retention time",
                icon: <Zap className="w-6 h-6" />
              },
              {
                step: 4,
                title: "Biogas Generation",
                desc: "Raw biogas (60-70% methane) captured from sealed digesters",
                icon: <Wind className="w-6 h-6" />
              },
              {
                step: 5,
                title: "Purification (PSA/VPSA)",
                desc: "CO₂, H₂S, and moisture stripped out to yield 95-98% pure biomethane",
                icon: <Flame className="w-6 h-6" />
              },
              {
                step: 6,
                title: "Compression & Dispensing",
                desc: "Gas compressed and bottled as Bio-CNG for vehicles or piped for industrial use",
                icon: <BarChart3 className="w-6 h-6" />
              },
              {
                step: 7,
                title: "Digestate / Biofertilizer",
                desc: "Residual slurry processed into organic compost/biofertilizer as revenue by-product",
                icon: <Leaf className="w-6 h-6" />
              }
            ].map((item) => (
              <div key={item.step} className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-lime-400 text-black font-bold text-2xl flex items-center justify-center">
                    {item.step}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-lime-400">{item.icon}</div>
                    <h3 className="font-heading text-xl md:text-2xl font-bold uppercase text-white">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-zinc-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Smart Technology Features */}
      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              Smart Technology <span className="text-lime-400">Features</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              What makes our Bio-CNG plant "smart" and industry-leading
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Cpu className="w-10 h-10 text-lime-400" />,
                title: "PLC/SCADA Automation",
                desc: "Fully automated feeding, mixing, temperature control, and gas flow regulation with minimal manual intervention"
              },
              {
                icon: <Zap className="w-10 h-10 text-lime-400" />,
                title: "IoT Remote Monitoring",
                desc: "Real-time dashboard (mobile & web) tracking gas pressure, methane %, pH, temperature, and flow rates"
              },
              {
                icon: <BarChart3 className="w-10 h-10 text-lime-400" />,
                title: "Predictive Maintenance",
                desc: "Sensors detect anomalies early, reducing downtime and emergency repair costs"
              },
              {
                icon: <Factory className="w-10 h-10 text-lime-400" />,
                title: "Centralized Control",
                desc: "Manage all plant zones (digester, purification, compression) from one interface"
              },
              {
                icon: <FileCheck className="w-10 h-10 text-lime-400" />,
                title: "Data Logging & Reporting",
                desc: "Automated production reports for compliance, audits, and SATAT/GOBARdhan scheme documentation"
              },
              {
                icon: <TrendingDown className="w-10 h-10 text-lime-400" />,
                title: "Optimized Yield",
                desc: "Real-time adjustments maximize biogas production and methane purity"
              }
            ].map((feature, idx) => (
              <div key={idx} className="bg-zinc-950 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors">
                <div className="mb-4">{feature.icon}</div>
                <h3 className="font-heading text-xl font-bold uppercase mb-3 text-white">
                  {feature.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Smart Matters - Comparison */}
      <section className="py-16 md:py-24 bg-zinc-950">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              Why <span className="text-lime-400">"Smart"</span> Matters
            </h2>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700 bg-zinc-800">
                  <th className="py-4 px-6 text-left font-heading uppercase text-white">Feature</th>
                  <th className="py-4 px-6 text-left font-heading uppercase text-zinc-400">Conventional Plant</th>
                  <th className="py-4 px-6 text-left font-heading uppercase text-lime-400">GreenPure Smart Plant</th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                <tr className="border-b border-zinc-800">
                  <td className="py-4 px-6 font-semibold">Operation</td>
                  <td className="py-4 px-6 text-red-400">Manual feeding & monitoring</td>
                  <td className="py-4 px-6 text-lime-400">✓ Fully automated PLC control</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-4 px-6 font-semibold">Visibility</td>
                  <td className="py-4 px-6 text-red-400">No remote visibility</td>
                  <td className="py-4 px-6 text-lime-400">✓ IoT dashboard on mobile/web</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-4 px-6 font-semibold">Maintenance</td>
                  <td className="py-4 px-6 text-red-400">Reactive maintenance</td>
                  <td className="py-4 px-6 text-lime-400">✓ Predictive alerts via sensors</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-4 px-6 font-semibold">Output</td>
                  <td className="py-4 px-6 text-red-400">Fixed output</td>
                  <td className="py-4 px-6 text-lime-400">✓ Optimized yield via real-time adjustments</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-semibold">Revenue</td>
                  <td className="py-4 px-6 text-red-400">Single revenue stream</td>
                  <td className="py-4 px-6 text-lime-400">✓ Gas + compost + carbon credits</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Environmental Impact */}
      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              Environmental & <span className="text-lime-400">Business Impact</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-zinc-950 border border-zinc-800 p-8">
              <TrendingDown className="w-12 h-12 text-lime-400 mb-4" />
              <h3 className="font-heading text-2xl font-bold uppercase mb-4 text-white">Carbon Credits Eligible</h3>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Registered under GOBARdhan/SATAT scheme for carbon credit certification
              </p>
              <ul className="space-y-2 text-zinc-300">
                <li>• 70-80% lower lifecycle emissions vs fossil CNG</li>
                <li>• CO₂ displacement through renewable Bio-CNG</li>
                <li>• Tradeable carbon credits revenue stream</li>
              </ul>
            </div>

            <div className="bg-zinc-950 border border-zinc-800 p-8">
              <Leaf className="w-12 h-12 text-lime-400 mb-4" />
              <h3 className="font-heading text-2xl font-bold uppercase mb-4 text-white">Zero Waste Model</h3>
              <p className="text-zinc-400 leading-relaxed mb-4">
                100% of feedstock converted with no landfill waste
              </p>
              <ul className="space-y-2 text-zinc-300">
                <li>• Bio-CNG for vehicles & industry</li>
                <li>• Organic biofertilizer from digestate</li>
                <li>• Multiple revenue streams</li>
              </ul>
            </div>

            <div className="bg-zinc-950 border border-zinc-800 p-8 md:col-span-2">
              <BarChart3 className="w-12 h-12 text-lime-400 mb-4" />
              <h3 className="font-heading text-2xl font-bold uppercase mb-4 text-white">Revenue Streams</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-zinc-300">
                <div>
                  <p className="font-semibold text-white mb-2">1. Bio-CNG Sales</p>
                  <p className="text-sm text-zinc-400">Primary revenue from purified biomethane</p>
                </div>
                <div>
                  <p className="font-semibold text-white mb-2">2. Biofertilizer Sales</p>
                  <p className="text-sm text-zinc-400">Organic compost by-product</p>
                </div>
                <div>
                  <p className="font-semibold text-white mb-2">3. Carbon Credits</p>
                  <p className="text-sm text-zinc-400">Emissions reduction certificates</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feedstock Section */}
      <section className="py-16 md:py-24 bg-zinc-950">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              Feedstock We <span className="text-lime-400">Process</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Locally sourced from Chikkaballapur region
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              "Agricultural residues (crop stalks, cane tops, corn stover)",
              "Cattle dung and poultry litter",
              "Press mud and distillery effluent",
              "Municipal organic waste (scale-up ready)"
            ].map((feedstock, idx) => (
              <div key={idx} className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors">
                <Leaf className="w-8 h-8 text-lime-400 mb-3" />
                <p className="text-zinc-300 leading-relaxed">{feedstock}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Compliance */}
      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              Trust & <span className="text-lime-400">Compliance</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: <FileCheck className="w-8 h-8" />, text: "SATAT scheme registered / application in progress" },
              { icon: <FileCheck className="w-8 h-8" />, text: "Compliance with CPCB and state pollution control norms" },
              { icon: <FileCheck className="w-8 h-8" />, text: "Biogas quality certificates (methane %, H₂S limits)" },
              { icon: <Users className="w-8 h-8" />, text: "Partnership with MNRE / state energy department" }
            ].map((item, idx) => (
              <div key={idx} className="bg-zinc-950 border border-zinc-800 p-6 flex items-start gap-4">
                <div className="text-lime-400 flex-shrink-0">{item.icon}</div>
                <p className="text-zinc-300 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 md:py-24 bg-gradient-to-br from-lime-400 to-lime-500">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase font-heading mb-6 text-black">
            Partner With Us
          </h2>
          <p className="text-black text-lg mb-8 max-w-2xl mx-auto">
            Explore opportunities in Bio-CNG offtake, feedstock partnership, or site visits
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="https://wa.me/918431598801?text=Hi%2C%20I%20want%20to%20know%20more%20about%20Smart%20Bio-CNG%20Plant"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-black text-lime-400 hover:bg-zinc-900 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#000000] transition-all hover:-translate-y-1"
            >
              <Download className="inline mr-2 w-5 h-5" />
              Schedule Site Visit
            </a>
            <a
              href="https://wa.me/918431598801?text=Hi%2C%20I%20want%20feedstock%20assessment%20for%20Bio-CNG"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-black text-lime-400 hover:bg-zinc-900 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#000000] transition-all hover:-translate-y-1"
            >
              <MapPin className="inline mr-2 w-5 h-5" />
              Get Feedstock Assessment
            </a>
            <Link
              to="/vendor-empanelment"
              className="inline-block bg-transparent border-2 border-black text-black hover:bg-black hover:text-lime-400 font-bold uppercase tracking-wider px-8 py-4 transition-colors"
            >
              Partner for Bio-CNG Offtake
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BioCNGPlantPage;
